<template>
	<div
		class="filter-bar-seo"
		:class="{
			'filter-bar-seo--hidden': isSearchTab && !isFiltersVisible,
		}"
		@click="$emit('click')"
	>
		<div class="container-fluid container-max-width">
			<div class="row-filter-bar">
				<FilterBarProviderSeo
					v-if="!onlyShowFilters"
					:collectionType="collectionType"
					:newPageType="newPageType"
					:currentFilters="currentFilters"
					:webLocale="languageStore.webLocale"
					:userProviders="userProviders"
					:selectionType="filterBarSelectionType"
				/>

				<slot />

				<div class="filter-bar-seo__additional">
					<FilterBarContentTypeSeo
						v-if="!onlyShowFilters"
						:collectionType="collectionType"
						:currentFilters="currentFilters"
						:showCatalogTab="popularPageExpActive"
					/>
					<FilterButtonSeo
						v-if="!onlyShowFilters"
						class="filter-bar-seo__filter-button"
						:collectionType="collectionType"
					/>

					<div class="filter-bar-seo__additional__heading filter-bar-seo__additional--long">
						<FontAwesomeIcon :icon="faFilter" />
						{{ $t('WEBAPP_FILTERS') }}
					</div>

					<HiddenHorizontalScrollbar class="scrollbar">
						<ChipButton
							class="filter-bar-seo__additional--long"
							:class="{ 'always-active': onlyShowFilters }"
							v-for="(item, key) of additionalFilters"
							:active="activeFilters[key]"
							:key="key"
						>
							<button @click="openPopover(key, $event)">
								{{ (item || {}).name }}&nbsp;
								<FontAwesomeIcon :icon="openedPopoverKey === key ? faChevronUp : faChevronDown" />
							</button>
						</ChipButton>
					</HiddenHorizontalScrollbar>

					<div class="filter-bar-seo__reset">
						<ResetButton
							:keepProvidersState="onlyShowFilters ? currentFilters.providers.length === 1 : false"
							:keepContentTypeState="isProviderPage || popularPageExpActive"
							@resetClicked="$emit('resetClicked')"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';

import { FilterProductionCountry, FilterRuntime, FilterVoteCount } from '@/lazy-load-components';
import { NewPageType } from '@/@types/graphql-types';

// sub-components specific application modules
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import ResetButton from '@/components/buttons/ResetButton.vue';
import ChipButton from '@/ui-components/ChipButton.vue';

import FilterAgeCertification from '@/components/filter-bar/FilterAgeCertification.vue';
import FilterBarGenres from '@/components/filter-bar/FilterBarGenres.vue';
import FilterButtonSeo from '@/components/filter-bar/seo/FilterButtonSeo.vue';
import FilterMonetizationTypes from '@/components/filter-bar/FilterMonetizationTypes.vue';
import FilterPresentationTypes from '@/components/filter-bar/FilterPresentationTypes.vue';
import FilterPrice from '@/components/filter-bar/FilterPrice.vue';
import FilterRating from '@/components/filter-bar/FilterRating.vue';
import FilterReleaseYear from '@/components/filter-bar/FilterReleaseYear.vue';

import { CollectionType } from '@/enums/collection-type';

import { FilterValues, getUsedFilters, isAnyFilterActive } from '@/helpers/filter-helper';
import { showProFeatures } from '@/helpers/pro-web-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import type { FilterCollection } from '@/stores/useFilterStoreTypes';

const FilterBarContentTypeSeo = defineAsyncComponent(
	() => import('@/components/filter-bar/seo/FilterBarContentTypeSeo.vue')
);

const FilterBarProviderSeo = defineAsyncComponent(() => import('@/components/filter-bar/seo/FilterBarProviderSeo.vue'));

import type Provider from '@/interfaces/provider';
import { faChevronDown, faChevronUp, faFilter } from '@fortawesome/free-solid-svg-icons';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { SnowplowFreemiumContext } from '@/helpers/tracking/providers';

import { PopoverHelper } from '@/helpers/vfm/popover-helper';

type FilterType<T> = { name: string; filters: T };

interface AdditionalFilters {
	releaseYear: FilterType<[typeof FilterReleaseYear]>;
	genres: FilterType<[typeof FilterBarGenres]>;
	price: FilterType<[typeof FilterPrice, typeof FilterMonetizationTypes, typeof FilterPresentationTypes]>;
	rating: FilterType<[typeof FilterRating, typeof FilterVoteCount?]>;
	prodCountries?: FilterType<[typeof FilterProductionCountry]>;
	runTime?: FilterType<[typeof FilterRuntime]>;
	ageRating?: FilterType<[typeof FilterAgeCertification]>;
}

const props = withDefaults(
	defineProps<{
		collectionType: CollectionType;
		currentFilters: FilterCollection;
		newPageType?: NewPageType | undefined;
		onlyShowFilters?: boolean;
		isProviderPage?: boolean;
		isFiltersVisible?: boolean;
		popularPageExpActive?: boolean;
	}>(),
	{
		onlyShowFilters: false,
		isProviderPage: false,
		isFiltersVisible: true,
		popularPageExpActive: false,
	}
);

defineEmits(['click', 'resetClicked']);

const constantStore = useConstantStore();
const languageStore = useLanguageStore();
const userStore = useUserStore();

const { t } = useI18n();

/**
 * helper to determine the affected filter keys.
 * omit filterComponent to get all possible affected filter keys.
 */
const additionalFilters = computed(() => {
	const baseFilters: AdditionalFilters = {
		releaseYear: { name: t('WEBAPP_RELEASEYEAR'), filters: [FilterReleaseYear] },
		genres: { name: t('WEBAPP_GENRES'), filters: [FilterBarGenres] },
		price: {
			name: t('WEBAPP_PRICE'),
			filters: [FilterPrice, FilterMonetizationTypes, FilterPresentationTypes],
		},
		rating: { name: t('WEBAPP_RATING'), filters: [FilterRating] },
	};

	if (showProFeatures(languageStore.webLocale)) {
		baseFilters.prodCountries = {
			name: t('WEBAPP_TITLE_DETAIL_PROD_COUNTRIES'),
			filters: [FilterProductionCountry],
		};
		baseFilters.runTime = { name: t('WEBAPP_RUNTIME'), filters: [FilterRuntime] };

		// if premium user - add imdb popularity filter to the popover
		baseFilters.rating.filters = [FilterRating, FilterVoteCount];
	}

	// decide if the age rating should be added to all additional filters
	if (constantStore.ageCertifications.hasCertifications) {
		baseFilters.ageRating = { name: t('WEBAPP_AGERATING'), filters: [FilterAgeCertification] };
	}

	return baseFilters;
});

const isSearchTab = computed(() => props.collectionType === CollectionType.SEARCH);
const isPopularPage = computed(() => props.collectionType === CollectionType.POPULAR);

const filterBarSelectionType = computed(() => (isPopularPage.value && !userStore.isLoggedIn ? 'bundle' : 'single'));

const filterValues = computed(() => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { FilterProviders, ...filterValues } = FilterValues;
	if (!constantStore.ageCertifications.hasCertifications) {
		delete filterValues['FilterAgeCertification'];
	}

	return filterValues;
});

// filtering out Justwatch-plex Provider as it's only used by partners.
const userProviders = computed(() =>
	constantStore.providers().filter(({ isVisibleFilterBar }: Provider) => isVisibleFilterBar)
);

// we only calculate the active filters, when the currentFilters have changed.
// otherwise we'll use up computation power, especially during navigation which affects INP.
const activeFilters = ref<Record<string, boolean>>({});
watch(
	() => props.currentFilters,
	() => {
		activeFilters.value = Object.keys(additionalFilters.value).reduce((root, key) => {
			const item = additionalFilters.value[key as keyof AdditionalFilters];
			root[key] = isAnyFilterActive(getUsedFilters(filterValues.value, item?.filters), props.currentFilters);
			return root;
		}, {} as Record<string, boolean>);
	},
	{ immediate: true }
);

onServerPrefetch(async () => await languageStore.fetchLocales());
onMounted(languageStore.fetchLocales);

const openedPopoverKey = ref<keyof AdditionalFilters | null>(null);
async function openPopover(additionalFilterKey: keyof AdditionalFilters, event: Event) {
	openedPopoverKey.value = additionalFilterKey;

	PopoverHelper.create({
		props: {
			escToClose: true,
			hideOverlay: true,
		},
		popoverProps: {
			cssClass: 'popover__filter',
			boundingElement: event.currentTarget,
		},
		component: {
			render: () => {
				const addFilters = additionalFilters.value[additionalFilterKey];

				if (addFilters?.filters == null) {
					return h('div', [h('div', { style: { padding: '10px' } }, 'N/A no filters yet')]);
				}

				return h(
					'div',
					addFilters.filters.map((item: any) =>
						h(item, {
							collectionType: props.collectionType,
							currentFilters: props.currentFilters,
						})
					)
				);
			},
		},
		onOpened: async () => {
			const contexts = [];
			if (userStore.isPremium) {
				contexts.push(new SnowplowFreemiumContext(userStore.isHideSeenDislikedTitlesEnabled ?? false));
			}

			TrackingHelper.trackEvent(
				'userinteraction',
				{
					action: 'filter_popover_toggled',
					label: additionalFilterKey,
				},
				contexts
			);
		},
		onClosed: () => {
			openedPopoverKey.value = null;
		},
	});
}
</script>

<style lang="scss">
@import '~@/styles/variables.scss';
@import '~@/styles/mixins.scss';

.popover__filter {
	width: 400px;
}

.filter-bar-seo {
	@include dark-blur-background();

	@media (max-width: $breakpoint-two-rows) {
		.row-filter-bar {
			margin: 0 15px;
		}
	}

	position: relative;
	width: 100%;
	z-index: 10;
	transition: transform 0.3s ease-out;

	&__reset {
		margin-inline-start: auto;
	}

	&__popover {
		transform: translateY(50px);
	}

	&__filter-button {
		@media (min-width: $screen-md-min) {
			display: none !important;
		}
		@media (max-width: $screen-md-min) {
			display: block;
		}
	}

	&__additional {
		position: relative;
		padding-bottom: 10px;
		display: flex;
		align-items: baseline;
		justify-content: flex-start;

		& > div {
			&:not(:last-child) {
				margin-inline-end: 10px;
			}
		}

		&__heading {
			white-space: nowrap;
			color: var(--jw-color-secondary);
			text-transform: uppercase;
			font-weight: 700;
		}

		.jw-chip-button {
			margin-bottom: 0;
		}

		.hidden-horizontal-scrollbar__items {
			margin: 0 !important;
			bottom: 0 !important;
		}

		&--long {
			&:not(:last-child) {
				margin-inline-end: 4px;
			}

			@media (max-width: $screen-md-min) {
				&.always-active {
					display: block !important;
				}
				display: none !important;
			}
		}
	}

	a {
		transition: opacity 0.2s ease;
		cursor: pointer;
	}

	&--inactive {
		a {
			opacity: 0.4;
		}
	}

	&--hidden {
		transform: translateY(-180px);
	}
}

.scrollbar {
	div {
		padding-left: 0 !important;
	}
}
</style>
